import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatDateMedPadded } from '@cf-shared/date-format';
import { resolveSubscriptionEndInstants } from '@cf-shared/data-model/subscription-end-instants';
import { UserDb } from 'services/user-db.service';
import { AuthService } from 'services/auth.service';
import { SettingsService } from 'services/settings.service';
import { ImageService } from 'services/image-service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { LoggingService } from 'services/logging.service';
import { PayProUnsubscribeResult, UnsubscribeReason } from '@cf-shared/activation-codes-interface';
import { getMyApp } from 'app/app-interface';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { IHomePage } from 'pages/home/home-interface';
import { getSupportEmail } from 'environments/environment-helpers';
import { sleep } from '@cf-shared/shared-util';
import { isTransportFailureAfterRetries } from 'services/cloud-functions-retry';
import { decideOnSubscriptionRecord, UnsubscribeRecordDecision } from './unsubscribe-record-policy';

/** Local sentinel used to synthesise an application-level error result
 *  for a transport-layer exhaustion (network or 5xx after wrapper
 *  retries). Lets the same alert + retry branch handle both paths. The
 *  value is internal — it never reaches the server or the i18n layer
 *  — and skips the trackSilentProblem report (the cloud-functions
 *  wrapper already reported the underlying transport error). */
const TRANSPORT_FAILURE_SENTINEL_ERROR_CODE = 'cs:transport-failure-after-retries';

@Component({
    selector: 'page-unsubscribe',
    templateUrl: 'unsubscribe.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class UnsubscribePage extends CsPage<{}> {
  protected LL = LL;

  reason: UnsubscribeReason;
  customReason: string = "";

  UnsubscribeReason = UnsubscribeReason;

  // a warm restart shows this page before its server login, so the page filter may
  // have let it open without knowing whether the person can cancel (df-e26cc1c4b021).
  // true from such an entry until the first server record arrives and is judged
  private decidedWithoutSubscriptionRecord = false;
  private subscriptionRecordListener: Subscription | undefined;

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public auth: AuthService,
    protected settings: SettingsService,
    protected imageService: ImageService,
    protected cloudFunctionsService: CloudFunctionsService,
    protected svPlanHandler: SvPlanHandlerService,
    public logging: LoggingService,
    public loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  /** true while the server's subscription record is not loaded. Nothing is cancelled
   *  meanwhile, because the page cannot yet tell whether there is anything to cancel */
  get waitingForSubscriptionRecord(): boolean {
    return !this.auth.couch;
  }

  // no entry check of its own (the page filter decides who may open it); this only
  // records how the page was entered, for the re-decision when the record arrives
  async ionViewCanEnter() {
    // this also runs when the page is uncovered. keep a re-decision that is still owed: a request
    // to leave that was dropped while the page was covered set the flag, and the record has
    // loaded by now (df-53c89a39b0e6)
    this.decidedWithoutSubscriptionRecord = this.decidedWithoutSubscriptionRecord || this.waitingForSubscriptionRecord;
  }

  async ionViewDidEnter() {
    // listen only if the page opened without the record. the source replays the
    // current record on subscribe, so a login that finished before now is seen too
    this.subscriptionRecordListener?.unsubscribe();
    this.subscriptionRecordListener = undefined;
    if (this.decidedWithoutSubscriptionRecord) {
      this.subscriptionRecordListener = this.auth.couchAuthChanged.subscribe(couchAuth => {
        if (couchAuth && this.decidedWithoutSubscriptionRecord) this.onServerSubscriptionRecord();
      });
    }
  }

  async ionViewWillLeave() {
    this.subscriptionRecordListener?.unsubscribe();
    this.subscriptionRecordListener = undefined;
  }

  // the first server record after a record-less entry. if it shows nothing to cancel,
  // leave once, as the page filter would have sent the person away; otherwise the
  // Unsubscribe button becomes usable
  private onServerSubscriptionRecord() {
    // one re-decision only; a later login must not navigate again
    this.decidedWithoutSubscriptionRecord = false;
    this.subscriptionRecordListener?.unsubscribe();
    this.subscriptionRecordListener = undefined;
    this.cdr.markForCheck();
    if (this.viewDestroyed) return;

    const decision = decideOnSubscriptionRecord({
      canCancelSubscription: this.auth.getCanCancelSubscription(),
    });
    if (decision === UnsubscribeRecordDecision.Stay) return;
    // ask rather than navigate: a login retry or warm resume Phase B may be moving the
    // person already, and a second navigation would run alongside theirs. the request
    // waits for them and is dropped if this page is no longer on screen by then; if it is
    // dropped, decide again when the page is shown again, since the page may only have been
    // covered (df-53c89a39b0e6, design-docs/warm-resume.md)
    console.log('Unsubscribe: the server login shows nothing to cancel; asking to leave the unsubscribe page');
    this.navCtrl.requestPostLoginRoute(
      'the unsubscribe page, with nothing to cancel',
      () => getMyApp().gotoDefaultPageAsync(),
      () => !this.viewDestroyed,
      () => { this.decidedWithoutSubscriptionRecord = true; },
    );
  }

  onCancelButtonClick(event: Event) {
    lockClick(event, this.navCtrl.popAsync());
  }

  async onUnsubscribeButtonClick() {
    // the button is disabled meanwhile; this only backs that up
    if (this.waitingForSubscriptionRecord) return;
    // "you keep access until <date>" asks when OUR ACCESS ends, so this is the
    // access answer — the stored value as written, with no grace taken off.
    // Routed through the shared helper rather than parsed here so this site
    // cannot drift from the rest (#4278). It also handles the indefinite
    // ('never') and unreadable cases that used to be tested for by hand.
    const { expiry_utc } = this.auth.getAuthRoleAndExpiry();
    const { accessEndsAt } = resolveSubscriptionEndInstants(undefined, expiry_utc, {});
    const licenseExpiry = accessEndsAt ? formatDateMedPadded(accessEndsAt.toLocal(), BOOT_LOCALE) : '';

    // Outer loop covers user-initiated Retry from the failure alert.
    // Each iteration: one call + one auto-retry-after-2s + branch on
    // outcome. We loop only when the user explicitly taps Retry; the
    // 2s auto-retry inside the helper is for the transient
    // Subscriptions/Suspend failures (Bug 10, Sentry CYBERNETIC-FITNESS-APP-42Y)
    // — usually one wait-then-retry is enough.
    while (true) {
      const loading = this.loadingCtrl.create({});
      await loading.present();

      let result: PayProUnsubscribeResult;
      try {
        result = await this.attemptUnsubscribeWithAutoRetry();
      } catch (error) {
        // Transport failures the cloud-functions wrapper has already
        // exhausted retries on (CloudFunctionNetworkError, or
        // CloudFunctionHttpError with httpStatus 502/503) are routed
        // through the same Retry / Contact-Support alert as the
        // application-level error path — otherwise the user just sees
        // the spinner dismiss with no feedback (the new dedup in
        // logging.service.ts intentionally swallows the abnormal-toast
        // for those classes because the wrapper already reported them
        // to Sentry). Any other error (CloudFunctionAuthError, raw
        // bugs, etc.) is genuinely abnormal and keeps the original
        // abnormalException path.
        if (isTransportFailureAfterRetries(error)) {
          // synthesise an application-level error result so the alert
          // + retry / contact-support branch below handles transport
          // failures uniformly with PayPro application failures.
          // TRANSPORT_FAILURE_SENTINEL_ERROR_CODE is also the signal
          // for skipping the trackSilentProblem report (the wrapper
          // already reported the underlying transport error).
          result = { error: TRANSPORT_FAILURE_SENTINEL_ERROR_CODE };
        } else {
          this.logging.trackAbnormalException(error, LL.errors.userFacing.unsubscribeFailed());
          await loading.dismiss();
          return;
        }
      }
      // dismiss before showing any alert/popup so the spinner doesn't
      // sit behind it. Loading.dismiss is idempotent (indexOf-guarded
      // splice in loading-controller.ts) so any path that loops back
      // and creates a new loader is safe.
      await loading.dismiss();
      if (this.viewDestroyed) return;

      if (result.alreadyCanceled) {
        await this.alertCtrl.showAlertAsync({
          message: LL.unsubscribe.alerts.alreadyCanceled(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
        });
        if (this.viewDestroyed) return;
        await this.navCtrl.setRootAsync(IHomePage, {});
        await getMyApp().reloadAppAsync({ resetToRoot: false });
        return;
      }

      if (result.error) {
        // trackSilentProblem prepends "PROBLEM: " itself (logging.service.ts),
        // so the name passed here should not. Report only after the
        // auto-retry has exhausted — transient PayPro failures recovered
        // by the inner retry should not pollute the Sentry rate. Skip
        // the report on transport failures: the cloud-functions wrapper
        // already reported them.
        if (result.error !== TRANSPORT_FAILURE_SENTINEL_ERROR_CODE) {
          this.logging.trackSilentProblem('PayProUnsubscribeError', {
            error: result.error,
            errorDetails: result.errorDetails,
          });
        }
        const shouldRetry = await this.alertCtrl.showAlertAsync<boolean>({
          title: LL.unsubscribe.alerts.unsubscribeFailed.title(),
          message: LL.unsubscribe.alerts.unsubscribeFailed.message(),
          // Backdrop-tap normally maps to the Cancel-role button, which
          // here would silently open the mail client — an unexpected
          // jump for a user who tapped outside merely to dismiss. Force
          // an explicit button tap.
          enableBackdropDismiss: false,
          buttons: [
            {
              text: LL.unsubscribe.alerts.unsubscribeFailed.contactSupport(),
              role: AlertButtonRole.Cancel,
              result: false,
            },
            {
              text: LL.unsubscribe.alerts.unsubscribeFailed.retry(),
              role: AlertButtonRole.Primary,
              result: true,
            },
          ],
        });
        if (this.viewDestroyed) return;
        if (shouldRetry) continue;
        // Contact Support: open the user's mail client at the standard
        // support address. Same convention as the support buttons on the
        // subscribe and login pages.
        window.location.href = `mailto:${getSupportEmail()}`;
        return;
      }

      // success path
      await this.alertCtrl.showAlertAsync({
        message: LL.unsubscribe.alerts.success({ date: licenseExpiry }),
        buttons: [
          {
            text: LL.common.buttons.ok(),
            role: AlertButtonRole.Primary
          },
        ],
      });
      if (this.viewDestroyed) return;
      await this.navCtrl.setRootAsync(IHomePage, {});
      await getMyApp().reloadAppAsync({ resetToRoot: false });
      return;
    }
  }

  /** Calls payProUnsubscribe once; on application-level error (`result.error`)
   *  waits 2s and tries once more. Bug 10 (Sentry CYBERNETIC-FITNESS-APP-42Y)
   *  is dominated by transient PayPro-side failures on Subscriptions/Suspend
   *  — a single same-request retry seconds later usually succeeds.
   *
   *  Network errors and HTTP 5xx are NOT handled here — the cloud-functions
   *  wrapper has its own resilient retry for those (see cloud-functions.service.ts
   *  `resilientCapped`). This auto-retry is specifically for application-level
   *  rejections that flow back as `{ error, errorDetails }` on a successful
   *  HTTP response. */
  private async attemptUnsubscribeWithAutoRetry(): Promise<PayProUnsubscribeResult> {
    const callArgs = {
      cancellationReasonId: this.reason,
      reasonText: this.customReason,
    };
    let result = await this.cloudFunctionsService.payProUnsubscribeAsync(callArgs);
    if (!result.error) return result;
    await sleep(2000);
    if (this.viewDestroyed) return result;
    return await this.cloudFunctionsService.payProUnsubscribeAsync(callArgs);
  }

}
